import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, FileText, TrendingUp, TrendingDown, Plug, LogOut, Activity, AlertCircle } from 'lucide-react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import UserManagement from '@/pages/UserManagement';
import { Logo } from '@/components/Logo';
import { EmailNotificationManager } from '@/components/EmailNotificationManager';
import { CostAnalyticsDashboard } from '@/components/CostAnalyticsDashboard';
import { ApiErrorLogViewer } from '@/components/ApiErrorLogViewer';
import { IntegrationSettingsManager } from '@/components/IntegrationSettingsManager';
import { AnnouncementManager } from '@/components/AnnouncementManager';
import { BulkEmailManager } from '@/components/BulkEmailManager';
import { PlatformSettingsManager } from '@/components/PlatformSettingsManager';
import { logger } from '@/lib/logger';

interface PlatformStats {
  totalUsers: number;
  activeNorths: number;
  totalNorths: number;
  newUsersThisMonth: number;
  downgradesThisMonth: number;
  apiRequestsToday: number;
  apiRequestsThisMonth: number;
  apiErrorsToday: number;
  activeApiKeys: number;
  cacheHitRate: number;
  internalApiRequestsToday: number;
  internalApiRequestsThisMonth: number;
  internalApiErrorsToday: number;
  internalApiActiveKeys: number;
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [syncingAirtable, setSyncingAirtable] = useState(false);
  const [executivePrompt, setExecutivePrompt] = useState("");
  const [showingsPrompt, setShowingsPrompt] = useState("");
  const [compsPrompt, setCompsPrompt] = useState("");
  const [neighborhoodPrompt, setNeighborhoodPrompt] = useState("");
  const [emailPrompt, setEmailPrompt] = useState("");
  const [agentInsightsPrompt, setAgentInsightsPrompt] = useState("");
  const [savingPrompts, setSavingPrompts] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [showErrorLogModal, setShowErrorLogModal] = useState(false);
  const [stats, setStats] = useState<PlatformStats>({
    totalUsers: 0,
    activeNorths: 0,
    totalNorths: 0,
    newUsersThisMonth: 0,
    downgradesThisMonth: 0,
    apiRequestsToday: 0,
    apiRequestsThisMonth: 0,
    apiErrorsToday: 0,
    activeApiKeys: 0,
    cacheHitRate: 0,
    internalApiRequestsToday: 0,
    internalApiRequestsThisMonth: 0,
    internalApiErrorsToday: 0,
    internalApiActiveKeys: 0,
  });

  useEffect(() => {
    checkAdminAndLoadStats();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('north_logo_url')
      .not('north_logo_url', 'is', null)
      .limit(1)
      .maybeSingle();
    
    if (data?.north_logo_url) {
      setLogoUrl(data.north_logo_url);
    }
  };

  const checkAdminAndLoadStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Only allow admin@northreports.com to access system admin
      if (user.email !== 'admin@northreports.com') {
        navigate('/dashboard');
        return;
      }

      setIsAdmin(true);
      await loadStats();
      await loadPrompts();
    } catch (error) {
      logger.error('Error checking admin status:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadPrompts = async () => {
    try {
      // Always load prompts from admin profile (system-wide defaults)
      const { data, error } = await supabase
        .from('profiles')
        .select('executive_summary_prompt, showings_summary_prompt, comps_summary_prompt, neighborhood_summary_prompt, email_prompt, agent_insights_prompt')
        .eq('email', 'admin@northreports.com')
        .maybeSingle();

      logger.log('Load prompts query result:', { data, error });

      if (error) throw error;
      
      // Default prompts from the edge functions
      const defaultExecutivePrompt = `You are an expert real estate marketing analyst who writes concise, data-driven executive summaries for property performance reports. 
Your goal is to summarize the key highlights and insights from the provided property metrics, social engagement data, and market trends. 
Write in a confident, professional tone suitable for seller reporting. 
Keep it conversational but data-focused (like a coach explaining performance). 
Use one short paragraph (120–160 words max). 
Bold any key numbers, trends, or actions to draw attention (e.g., **1,248 Zillow views**, **45 saves**, **price reduction**, **strong buyer interest**, **refresh marketing efforts**).`;

      const defaultShowingsPrompt = `You are a real estate expert analyzing showing activity and buyer interest patterns. 
Create a concise, data-driven summary (120-160 words) highlighting:
1. Overall showing activity and open house attendance trends
2. Key buyer feedback themes (positive or concerning)
3. Recommendations for next steps or adjustments

IMPORTANT: Private showings do NOT track attendee counts - only mention attendees for open houses.
Use **bold** for key numbers and insights.`;

      const defaultCompsPrompt = `You are a real estate expert analyzing market comparables and pricing strategies.`;

      const defaultNeighborhoodPrompt = `You are a real estate market analyst creating a neighborhood market summary.
Analyze the neighborhood data and create a concise, data-driven summary (120-160 words) highlighting:
1. Market activity and inventory levels (active listings, new listings)
2. Buyer demand indicators (pending sales, demand rating out of 10)
3. Market trends (buyer competition trend: Rising/Stable/Easing)
4. Key metrics like days on market and price reduction rates
5. Clear interpretation of what these metrics mean for sellers

Use **bold** for key numbers and insights. Keep the tone professional and actionable.`;

      const defaultEmailPrompt = `You are a professional real estate agent writing an email update to a home seller. Create a warm, professional email that summarizes recent activity and market insights for their listing. Use the provided highlights to create 3-5 key bullet points. Keep the tone conversational but professional.`;

      const defaultAgentInsightsPrompt = `You are an expert real estate advisor providing strategic insights to agents. Based on property details and the agent's assessment, provide actionable recommendations.

Your response should be structured with the following sections:
1. **Summary**: A brief overview of the current situation (2-3 sentences)
2. **Key Insights**: 3-5 bullet points highlighting critical observations
3. **Recommendations**: Specific, actionable next steps the agent should take
4. **Potential Risks**: Any concerns or red flags to watch for
5. **Action Timeline**: Suggested immediate (next 48 hours), short-term (this week), and medium-term (this month) actions

Be specific, practical, and focused on actions the agent can take. Use professional but clear language. Bold key points and action items for emphasis.`;
      
      if (data) {
        setExecutivePrompt(data.executive_summary_prompt || defaultExecutivePrompt);
        setShowingsPrompt(data.showings_summary_prompt || defaultShowingsPrompt);
        setCompsPrompt(data.comps_summary_prompt || defaultCompsPrompt);
        setNeighborhoodPrompt(data.neighborhood_summary_prompt || defaultNeighborhoodPrompt);
        setEmailPrompt(data.email_prompt || defaultEmailPrompt);
        setAgentInsightsPrompt(data.agent_insights_prompt || defaultAgentInsightsPrompt);
      }
    } catch (error) {
      logger.error('Error loading prompts:', error);
    }
  };

  const syncAirtable = async (northId?: string) => {
    setSyncingAirtable(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      if (!northId) {
        const { data: norths, error: northsError } = await supabase
          .from('norths')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (northsError) throw northsError;
        if (!norths) {
          toast({
            title: "No properties found",
            description: "Please create a North property first",
            variant: "destructive",
          });
          return;
        }
        northId = norths.id;
      }

      const { data, error } = await supabase.functions.invoke('sync-airtable', {
        body: { northId },
      });

      if (error) throw error;

      toast({
        title: "Sync Complete",
        description: data.message || "Airtable data synced successfully",
      });
    } catch (error: any) {
      logger.error('Error syncing Airtable:', error);
      toast({
        title: "Sync Failed",
        description: error.message || "Failed to sync Airtable data",
        variant: "destructive",
      });
    } finally {
      setSyncingAirtable(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (error) {
      logger.error('Error logging out:', error);
      toast({
        title: "Error",
        description: "Failed to log out",
        variant: "destructive",
      });
    }
  };

  const savePrompts = async () => {
    setSavingPrompts(true);
    try {
      logger.log('Saving prompts:', {
        executivePrompt,
        showingsPrompt,
        compsPrompt,
        neighborhoodPrompt,
        emailPrompt,
        agentInsightsPrompt
      });

      // Get current user (must be admin@northreports.com)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      logger.log('Current user:', user.id, user.email);

      // Save prompts to current user's profile (system admin)
      const { data, error } = await supabase
        .from('profiles')
        .update({
          executive_summary_prompt: executivePrompt || null,
          showings_summary_prompt: showingsPrompt || null,
          comps_summary_prompt: compsPrompt || null,
          neighborhood_summary_prompt: neighborhoodPrompt || null,
          email_prompt: emailPrompt || null,
          agent_insights_prompt: agentInsightsPrompt || null,
        })
        .eq('user_id', user.id)
        .select();

      logger.log('Save prompts result:', { data, error });

      if (error) throw error;

      toast({
        title: "Success",
        description: "AI prompts saved successfully",
      });

      await loadPrompts();
    } catch (error) {
      logger.error('Error saving prompts:', error);
      toast({
        title: "Error",
        description: "Failed to save prompts",
        variant: "destructive",
      });
    } finally {
      setSavingPrompts(false);
    }
  };

  const loadStats = async () => {
    try {
      // Total users
      const { count: totalUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // Active norths (published)
      const { count: activeNorths } = await supabase
        .from('norths')
        .select('*', { count: 'exact', head: true })
        .eq('is_published', true);

      // Total norths ever created
      const { count: totalNorths } = await supabase
        .from('norths')
        .select('*', { count: 'exact', head: true });

      // New users this month
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: newUsersThisMonth } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startOfMonth.toISOString());

      // Downgrades this month (subscriptions that were canceled or have pending downgrade)
      const { data: subscriptionChanges } = await supabase
        .from('team_subscriptions')
        .select('plan_tier, status, pending_downgrade_tier, updated_at')
        .or('status.eq.canceled,status.eq.canceling,pending_downgrade_tier.not.is.null')
        .gte('updated_at', startOfMonth.toISOString());

      const downgradesThisMonth = subscriptionChanges?.length || 0;

      // API Usage Stats
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      // Try to get API request logs (table might not exist yet)
      let apiRequestsToday = 0;
      let apiRequestsThisMonth = 0;
      let apiErrorsToday = 0;
      let cacheHitRate = 0;

      try {
        // Requests today
        const { count: todayCount } = await supabase
          .from('api_call_logs')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfToday.toISOString());
        apiRequestsToday = todayCount || 0;

        // Requests this month
        const { count: monthCount } = await supabase
          .from('api_call_logs')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString());
        apiRequestsThisMonth = monthCount || 0;

        // Errors today
        const { count: errorCount } = await supabase
          .from('api_call_logs')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfToday.toISOString())
          .gte('status_code', 400);
        apiErrorsToday = errorCount || 0;

        // Cache hit rate (this month)
        // @ts-expect-error - Type instantiation issue with complex query
        const { count: totalRequests } = await supabase
          .from('api_call_logs')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString())
          .eq('service_name', 'zillow');

        // @ts-expect-error - Type instantiation issue with complex query
        const { count: cacheHits } = await supabase
          .from('api_call_logs')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString())
          .eq('service_name', 'zillow')
          .eq('is_cache_hit', true);

        if (totalRequests && totalRequests > 0) {
          cacheHitRate = Math.round((cacheHits || 0) / totalRequests * 100);
        }
      } catch (err) {
        // Table might not exist yet, that's ok
        logger.log('API call logs table not available yet');
      }

      // Active API keys (for external APIs)
      const { count: activeApiKeys } = await supabase
        .from('api_keys' as any)
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      // Internal API (North API) Stats - from api_request_logs
      let internalApiRequestsToday = 0;
      let internalApiRequestsThisMonth = 0;
      let internalApiErrorsToday = 0;
      let internalApiActiveKeys = 0;

      try {
        // Internal API requests today
        const { count: internalTodayCount } = await supabase
          .from('api_request_logs' as any)
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfToday.toISOString());
        internalApiRequestsToday = internalTodayCount || 0;

        // Internal API requests this month
        const { count: internalMonthCount } = await supabase
          .from('api_request_logs' as any)
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfMonth.toISOString());
        internalApiRequestsThisMonth = internalMonthCount || 0;

        // Internal API errors today (4xx/5xx status codes)
        const { count: internalErrorCount } = await supabase
          .from('api_request_logs' as any)
          .select('*', { count: 'exact', head: true })
          .gte('created_at', startOfToday.toISOString())
          .gte('status_code', 400);
        internalApiErrorsToday = internalErrorCount || 0;

        // Active API keys (for North API) - same as external, just showing for clarity
        const { count: internalActiveKeys } = await supabase
          .from('api_keys' as any)
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true);
        internalApiActiveKeys = internalActiveKeys || 0;
      } catch (err) {
        // Table might not exist yet, that's ok
        logger.log('API request logs table not available yet');
      }

      setStats({
        totalUsers: totalUsers || 0,
        activeNorths: activeNorths || 0,
        totalNorths: totalNorths || 0,
        newUsersThisMonth: newUsersThisMonth || 0,
        downgradesThisMonth,
        apiRequestsToday,
        apiRequestsThisMonth,
        apiErrorsToday,
        activeApiKeys: activeApiKeys || 0,
        cacheHitRate,
        internalApiRequestsToday,
        internalApiRequestsThisMonth,
        internalApiErrorsToday,
        internalApiActiveKeys,
      });
    } catch (error) {
      logger.error('Error loading stats:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-8" style={{
      '--primary': '0 0% 20%',
      '--primary-foreground': '0 0% 98%',
      '--ring': '0 0% 20%'
    } as React.CSSProperties}>
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex-1" />
          <div className="flex-1 flex justify-center">
            <Logo customLogoUrl={logoUrl} size="lg" />
          </div>
          <div className="flex-1 flex justify-end">
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
        
        <div>
          <h1 className="text-4xl font-bold">System Admin Dashboard</h1>
          <p className="text-muted-foreground mt-2">Platform statistics and management</p>
        </div>

        <Tabs defaultValue="statistics" className="space-y-6">
          <TabsList className="grid w-full grid-cols-8">
            <TabsTrigger value="statistics">Statistics</TabsTrigger>
            <TabsTrigger value="costs">Cost Analytics</TabsTrigger>
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
            <TabsTrigger value="announcements">Announcements</TabsTrigger>
            <TabsTrigger value="emails">Email Notifications</TabsTrigger>
            <TabsTrigger value="bulk-email">Bulk Email</TabsTrigger>
            <TabsTrigger value="platform">Platform Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="statistics" className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalUsers}</div>
                  <p className="text-xs text-muted-foreground">Registered on platform</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active North Reports</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.activeNorths}</div>
                  <p className="text-xs text-muted-foreground">Currently published</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total North Reports</CardTitle>
                  <FileText className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalNorths}</div>
                  <p className="text-xs text-muted-foreground">All time (including unpublished)</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">New Users This Month</CardTitle>
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.newUsersThisMonth}</div>
                  <p className="text-xs text-muted-foreground">Since {new Date().toLocaleString('default', { month: 'long' })} 1st</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Downgrades This Month</CardTitle>
                  <TrendingDown className="h-4 w-4 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.downgradesThisMonth}</div>
                  <p className="text-xs text-muted-foreground">Subscription canceled</p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-8">
              <h2 className="text-2xl font-semibold mb-4">Subscribed API Usage</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">API Requests Today</CardTitle>
                    <Activity className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.apiRequestsToday}</div>
                    <p className="text-xs text-muted-foreground">Total API calls today</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">API Requests This Month</CardTitle>
                    <Activity className="h-4 w-4 text-blue-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.apiRequestsThisMonth}</div>
                    <p className="text-xs text-muted-foreground">Since {new Date().toLocaleString('default', { month: 'long' })} 1st</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Cache Hit Rate</CardTitle>
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.cacheHitRate}%</div>
                    <p className="text-xs text-muted-foreground">Zillow API calls saved</p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setShowErrorLogModal(true)}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">API Errors Today</CardTitle>
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.apiErrorsToday}</div>
                    <p className="text-xs text-muted-foreground">4xx/5xx responses (click to view)</p>
                  </CardContent>
                </Card>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-2xl font-semibold mb-4">Internal API</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">API Requests Today</CardTitle>
                    <Activity className="h-4 w-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.internalApiRequestsToday}</div>
                    <p className="text-xs text-muted-foreground">North API calls today</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">API Requests This Month</CardTitle>
                    <Activity className="h-4 w-4 text-purple-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.internalApiRequestsThisMonth}</div>
                    <p className="text-xs text-muted-foreground">Since {new Date().toLocaleString('default', { month: 'long' })} 1st</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active API Keys</CardTitle>
                    <TrendingUp className="h-4 w-4 text-green-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.internalApiActiveKeys}</div>
                    <p className="text-xs text-muted-foreground">Keys in use</p>
                  </CardContent>
                </Card>

                <Card className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setShowErrorLogModal(true)}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">API Errors Today</CardTitle>
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.internalApiErrorsToday}</div>
                    <p className="text-xs text-muted-foreground">4xx/5xx responses (click to view)</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="costs">
            <CostAnalyticsDashboard />
          </TabsContent>

          <TabsContent value="users">
            <UserManagement />
          </TabsContent>

          <TabsContent value="integrations" className="space-y-6">
            <IntegrationSettingsManager />
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plug className="h-5 w-5" />
                  Integrations
                </CardTitle>
                <CardDescription>
                  External services and API configurations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h3 className="font-medium">Configured Integrations</h3>
                  <div className="grid gap-2">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">OpenAI</span>
                      <Badge>Active</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Resend (Email)</span>
                      <Badge>Active</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Polar (Payments)</span>
                      <Badge>Active</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">RapidAPI (Realtor.com Data)</span>
                      <Badge>Active</Badge>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <span className="text-sm">Firecrawl (Web Scraping)</span>
                      <Badge>Active</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">Airtable Sync</h3>
                    <Button
                      onClick={() => syncAirtable()}
                      disabled={syncingAirtable}
                      size="sm"
                    >
                      {syncingAirtable ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Syncing...
                        </>
                      ) : (
                        'Sync Now'
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Sync platform data with Airtable for reporting and analysis
                  </p>
                </div>

                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-medium">AI Prompt Customization</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="executive-prompt">Executive Summary Prompt</Label>
                    <Textarea
                      id="executive-prompt"
                      value={executivePrompt}
                      onChange={(e) => setExecutivePrompt(e.target.value)}
                      rows={4}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="showings-prompt">Showings Summary Prompt</Label>
                    <Textarea
                      id="showings-prompt"
                      value={showingsPrompt}
                      onChange={(e) => setShowingsPrompt(e.target.value)}
                      rows={4}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="comps-prompt">Comps Summary Prompt</Label>
                    <Textarea
                      id="comps-prompt"
                      value={compsPrompt}
                      onChange={(e) => setCompsPrompt(e.target.value)}
                      rows={4}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="neighborhood-prompt">Neighborhood Summary Prompt</Label>
                    <Textarea
                      id="neighborhood-prompt"
                      value={neighborhoodPrompt}
                      onChange={(e) => setNeighborhoodPrompt(e.target.value)}
                      rows={4}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email-prompt">Email Generation Prompt</Label>
                    <Textarea
                      id="email-prompt"
                      value={emailPrompt}
                      onChange={(e) => setEmailPrompt(e.target.value)}
                      rows={4}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="agent-insights-prompt">Agent Insights Prompt</Label>
                    <Textarea
                      id="agent-insights-prompt"
                      value={agentInsightsPrompt}
                      onChange={(e) => setAgentInsightsPrompt(e.target.value)}
                      rows={4}
                      className="font-mono text-xs"
                    />
                  </div>

                  <Button 
                    onClick={savePrompts} 
                    disabled={savingPrompts}
                    className="w-full"
                  >
                    {savingPrompts ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save All Prompts'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="announcements" className="space-y-6">
            <AnnouncementManager />
          </TabsContent>

          <TabsContent value="emails" className="space-y-6">
            <EmailNotificationManager />
          </TabsContent>

          <TabsContent value="bulk-email" className="space-y-6">
            <BulkEmailManager />
          </TabsContent>

          <TabsContent value="platform" className="space-y-6">
            <PlatformSettingsManager />
          </TabsContent>
        </Tabs>
      </div>

      {/* API Error Log Modal */}
      <ApiErrorLogViewer
        open={showErrorLogModal}
        onOpenChange={setShowErrorLogModal}
      />
    </div>
  );
}
